import React, { useState, useEffect, useRef } from 'react';
import { 
  Music, Play, Pause, Download, ShoppingCart, Search, 
  Filter, ChevronDown, Heart, Share2, Clock, Disc,
  Volume2, VolumeX, SkipBack, SkipForward, X, Check
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// License tier colors
const tierColors = {
  Personal: 'from-blue-500 to-cyan-500',
  Commercial: 'from-purple-500 to-pink-500',
  Broadcast: 'from-orange-500 to-red-500'
};

// Audio Player Component
const AudioPlayer = ({ song, isPlaying, onPlayPause, onClose }) => {
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const playPromiseRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      // Wait for any pending pause to complete, then play
      const playAudio = async () => {
        try {
          // If there's a pending play promise, wait for it
          if (playPromiseRef.current) {
            await playPromiseRef.current;
          }
          playPromiseRef.current = audio.play();
          await playPromiseRef.current;
        } catch (e) {
          // Ignore AbortError - this happens when play is interrupted
          if (e.name !== 'AbortError') {
            console.error('Audio play error:', e);
          }
        }
      };
      playAudio();
    } else {
      // Pause safely
      const pauseAudio = async () => {
        try {
          if (playPromiseRef.current) {
            await playPromiseRef.current;
          }
          audio.pause();
        } catch (e) {
          // Ignore errors during pause
        }
      };
      pauseAudio();
    }
  }, [isPlaying, song]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = percent * duration;
    }
  };

  if (!song) return null;

  // Build full audio URL
  const audioSrc = song.preview_url || song.full_audio_url;
  const fullAudioUrl = audioSrc?.startsWith('http') ? audioSrc : `${API_URL}${audioSrc}`;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gunmetal-900 border-t border-steel-700/50 p-4 z-40">
      <audio
        ref={audioRef}
        src={fullAudioUrl}
        onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={() => onPlayPause(false)}
        crossOrigin="anonymous"
      />
      
      <div className="container mx-auto flex items-center space-x-6">
        {/* Song Info */}
        <div className="flex items-center space-x-4 w-64">
          <div className="w-14 h-14 bg-gunmetal-700 rounded-lg overflow-hidden">
            {song.cover_art_url ? (
              <img src={song.cover_art_url} alt={song.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Disc className="text-accent-red" size={24} />
              </div>
            )}
          </div>
          <div className="overflow-hidden">
            <h4 className="text-white font-semibold truncate">{song.title}</h4>
            <p className="text-steel-400 text-sm truncate">{song.artist}</p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex-1 flex flex-col items-center">
          <div className="flex items-center space-x-4 mb-2">
            <button className="text-steel-400 hover:text-white transition-colors">
              <SkipBack size={20} />
            </button>
            <button
              onClick={() => onPlayPause(!isPlaying)}
              className="w-10 h-10 bg-accent-red rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-1" />}
            </button>
            <button className="text-steel-400 hover:text-white transition-colors">
              <SkipForward size={20} />
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full flex items-center space-x-3">
            <span className="text-steel-400 text-xs w-10 text-right">{formatTime(currentTime)}</span>
            <div 
              className="flex-1 h-1 bg-gunmetal-700 rounded-full cursor-pointer group"
              onClick={handleSeek}
            >
              <div 
                className="h-full bg-accent-red rounded-full relative group-hover:bg-red-500 transition-colors"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <span className="text-steel-400 text-xs w-10">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Volume & Actions */}
        <div className="flex items-center space-x-4 w-48 justify-end">
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="text-steel-400 hover:text-white transition-colors"
          >
            {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
          <button
            onClick={onClose}
            className="text-steel-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

// License Modal Component
const LicenseModal = ({ song, onClose }) => {
  const [selectedTier, setSelectedTier] = useState(null);

  const tiers = [
    {
      name: "Personal",
      price: 9.99,
      description: "For personal projects, home videos, and non-commercial use",
      rights: ["Personal projects", "Home videos", "Student projects", "Non-monetized content"]
    },
    {
      name: "Commercial",
      price: 49.99,
      description: "For business use, advertisements, and monetized content",
      rights: ["All Personal rights", "YouTube monetization", "Podcasts", "Corporate videos", "Advertisements", "Apps & Games"]
    },
    {
      name: "Broadcast",
      price: 199.99,
      description: "For TV, film, and large-scale broadcast use",
      rights: ["All Commercial rights", "TV broadcasts", "Film & documentaries", "Radio", "Streaming platforms", "Unlimited reproductions"]
    }
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
      <div className="bg-gunmetal-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-steel-700/50">
        {/* Header */}
        <div className="p-6 border-b border-steel-700/50 flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold text-white">{song.title}</h3>
            <p className="text-steel-400">{song.artist} • {song.genre}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-gunmetal-700 hover:bg-accent-red rounded-full flex items-center justify-center text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* License Tiers */}
        <div className="p-6">
          <h4 className="text-lg font-semibold text-white mb-4">Select License Type</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                onClick={() => setSelectedTier(tier.name)}
                className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all duration-300 ${
                  selectedTier === tier.name 
                    ? 'border-accent-red bg-accent-red/10' 
                    : 'border-steel-700/50 hover:border-steel-500'
                }`}
              >
                {selectedTier === tier.name && (
                  <div className="absolute top-4 right-4 w-6 h-6 bg-accent-red rounded-full flex items-center justify-center">
                    <Check size={14} className="text-white" />
                  </div>
                )}
                
                <div className={`text-sm font-bold uppercase tracking-wider mb-2 text-transparent bg-clip-text bg-gradient-to-r ${tierColors[tier.name]}`}>
                  {tier.name}
                </div>
                <div className="text-3xl font-black text-white mb-2">${tier.price}</div>
                <p className="text-steel-400 text-sm mb-4">{tier.description}</p>
                
                <ul className="space-y-2">
                  {tier.rights.map((right, i) => (
                    <li key={i} className="flex items-center text-sm text-steel-300">
                      <Check size={14} className="text-green-500 mr-2 flex-shrink-0" />
                      {right}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-steel-700/50">
            <a 
              href="#contact" 
              onClick={onClose}
              className="text-steel-400 hover:text-white transition-colors"
            >
              Need custom licensing? Contact us
            </a>
            <div className="flex space-x-4">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                className="bg-accent-red hover:bg-red-600 text-white"
                disabled={!selectedTier}
              >
                <ShoppingCart size={18} className="mr-2" />
                Purchase License - ${selectedTier ? tiers.find(t => t.name === selectedTier)?.price : '0.00'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Song Card Component
const SongCard = ({ song, onPlay, isPlaying, currentSong, onLicense }) => {
  const isCurrentSong = currentSong?.id === song.id;

  return (
    <div className="group bg-gunmetal-800/50 hover:bg-gunmetal-800 rounded-xl p-4 transition-all duration-300 border border-transparent hover:border-steel-700/50">
      <div className="flex items-center space-x-4">
        {/* Cover Art */}
        <div className="relative w-16 h-16 bg-gunmetal-700 rounded-lg overflow-hidden flex-shrink-0">
          {song.cover_art_url ? (
            <img src={song.cover_art_url} alt={song.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent-red/20 to-purple-500/20">
              <Disc className="text-accent-red" size={24} />
            </div>
          )}
          
          {/* Play Button Overlay */}
          <button
            onClick={() => onPlay(song)}
            className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {isCurrentSong && isPlaying ? (
              <Pause className="text-white" size={24} />
            ) : (
              <Play className="text-white ml-1" size={24} />
            )}
          </button>
        </div>

        {/* Song Info */}
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-semibold truncate">{song.title}</h4>
          <p className="text-steel-400 text-sm truncate">{song.artist}</p>
          <div className="flex items-center space-x-3 mt-1">
            <span className="text-steel-500 text-xs">{song.genre}</span>
            {song.bpm && <span className="text-steel-500 text-xs">{song.bpm} BPM</span>}
            <span className="text-steel-500 text-xs flex items-center">
              <Clock size={12} className="mr-1" />
              {song.duration}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button className="w-8 h-8 flex items-center justify-center text-steel-400 hover:text-accent-red transition-colors">
            <Heart size={18} />
          </button>
          <button className="w-8 h-8 flex items-center justify-center text-steel-400 hover:text-white transition-colors">
            <Share2 size={18} />
          </button>
          <Button
            size="sm"
            className="bg-accent-red hover:bg-red-600 text-white text-xs"
            onClick={() => onLicense(song)}
          >
            <Download size={14} className="mr-1" />
            License
          </Button>
        </div>
      </div>

      {/* Tags */}
      {song.mood && song.mood.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {song.mood.slice(0, 3).map((mood, i) => (
            <span key={i} className="px-2 py-1 bg-gunmetal-700 rounded-full text-xs text-steel-400">
              {mood}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Music Licensing Component
export const MusicLicensing = () => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedMood, setSelectedMood] = useState('');
  const [genres, setGenres] = useState([]);
  const [moods, setMoods] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Player state
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // License modal
  const [licenseSong, setLicenseSong] = useState(null);

  // Fetch songs
  useEffect(() => {
    fetchSongs();
    fetchGenres();
    fetchMoods();
  }, [selectedGenre, selectedMood, searchTerm]);

  const fetchSongs = async () => {
    try {
      let url = `${API_URL}/api/music/songs?limit=50`;
      if (selectedGenre) url += `&genre=${selectedGenre}`;
      if (selectedMood) url += `&mood=${selectedMood}`;
      if (searchTerm) url += `&search=${searchTerm}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setSongs(data.songs || []);
    } catch (error) {
      console.error('Error fetching songs:', error);
      // Use mock data if API fails
      setSongs(mockSongs);
    } finally {
      setLoading(false);
    }
  };

  const fetchGenres = async () => {
    try {
      const response = await fetch(`${API_URL}/api/music/genres`);
      const data = await response.json();
      setGenres(data.genres || defaultGenres);
    } catch {
      setGenres(defaultGenres);
    }
  };

  const fetchMoods = async () => {
    try {
      const response = await fetch(`${API_URL}/api/music/moods`);
      const data = await response.json();
      setMoods(data.moods || defaultMoods);
    } catch {
      setMoods(defaultMoods);
    }
  };

  const handlePlay = (song) => {
    if (currentSong?.id === song.id) {
      setIsPlaying(!isPlaying);
    } else {
      setCurrentSong(song);
      setIsPlaying(true);
    }
  };

  return (
    <>
      {/* License Modal */}
      {licenseSong && (
        <LicenseModal song={licenseSong} onClose={() => setLicenseSong(null)} />
      )}

      <section id="music-licensing" className="relative py-24 bg-gradient-to-b from-gunmetal-950 via-gunmetal-900 to-gunmetal-950">
        {/* Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent-red/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent-red/20 border border-accent-red/30 rounded-full mb-6">
              <Music className="text-accent-red" size={20} />
              <span className="text-accent-red font-semibold uppercase tracking-wider text-sm">Music Licensing</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-red via-orange-500 to-yellow-500">
                MINDESIGN SOUNDSCAPES
              </span>
            </h2>
            <p className="text-steel-400 max-w-2xl mx-auto text-lg">
              250+ royalty-free tracks ready for your next project. Preview, license, and download instantly.
            </p>
          </div>

          {/* Search & Filters */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-steel-500" size={20} />
                <Input
                  type="text"
                  placeholder="Search songs, artists, or tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 bg-gunmetal-800 border-steel-700/50 text-white placeholder:text-steel-500 h-12"
                />
              </div>

              {/* Filter Toggle */}
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="border-steel-700/50 text-steel-300 hover:bg-gunmetal-800"
              >
                <Filter size={18} className="mr-2" />
                Filters
                <ChevronDown size={18} className={`ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </Button>
            </div>

            {/* Filter Options */}
            {showFilters && (
              <div className="mt-4 p-4 bg-gunmetal-800/50 rounded-xl border border-steel-700/30 flex flex-wrap gap-4">
                <div>
                  <label className="text-steel-400 text-sm mb-2 block">Genre</label>
                  <select
                    value={selectedGenre}
                    onChange={(e) => setSelectedGenre(e.target.value)}
                    className="bg-gunmetal-700 border border-steel-700/50 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="">All Genres</option>
                    {genres.map((genre) => (
                      <option key={genre} value={genre}>{genre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-steel-400 text-sm mb-2 block">Mood</label>
                  <select
                    value={selectedMood}
                    onChange={(e) => setSelectedMood(e.target.value)}
                    className="bg-gunmetal-700 border border-steel-700/50 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="">All Moods</option>
                    {moods.map((mood) => (
                      <option key={mood} value={mood}>{mood}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between mb-8 pb-4 border-b border-steel-700/30">
            <p className="text-steel-400">
              <span className="text-white font-bold">{songs.length}</span> tracks available
            </p>
            <div className="flex items-center space-x-4 text-sm text-steel-400">
              <span>Sort by: <span className="text-white">Most Recent</span></span>
            </div>
          </div>

          {/* Songs Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-accent-red border-t-transparent"></div>
            </div>
          ) : songs.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {songs.map((song) => (
                <SongCard
                  key={song.id}
                  song={song}
                  onPlay={handlePlay}
                  isPlaying={isPlaying}
                  currentSong={currentSong}
                  onLicense={setLicenseSong}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Music className="mx-auto text-steel-600 mb-4" size={48} />
              <p className="text-steel-400">No songs found matching your criteria</p>
            </div>
          )}

          {/* Load More */}
          {songs.length >= 20 && (
            <div className="text-center mt-12">
              <Button className="bg-gunmetal-700 hover:bg-gunmetal-600 text-white px-8">
                Load More Tracks
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Audio Player */}
      {currentSong && (
        <AudioPlayer
          song={currentSong}
          isPlaying={isPlaying}
          onPlayPause={setIsPlaying}
          onClose={() => {
            setCurrentSong(null);
            setIsPlaying(false);
          }}
        />
      )}
    </>
  );
};

// Default genres and moods
const defaultGenres = ['Electronic', 'Hip Hop', 'Rock', 'Ambient', 'Cinematic', 'Pop', 'R&B', 'Jazz', 'Classical', 'World'];
const defaultMoods = ['Energetic', 'Chill', 'Dark', 'Uplifting', 'Dramatic', 'Happy', 'Sad', 'Mysterious', 'Romantic', 'Intense'];

// Mock songs for initial display
const mockSongs = [
  {
    id: 'shine-001',
    title: 'THE SHINE',
    artist: 'THE REDACTED',
    genre: 'Electronic',
    bpm: 128,
    duration: '4:32',
    mood: ['Energetic', 'Dark', 'Cinematic'],
    full_audio_url: `${API_URL}/api/uploads/files/THE_SHINE.mp3`,
    cover_art_url: `${API_URL}/api/uploads/files/REDACTED_band_live.png`
  },
  {
    id: 'breathing-002',
    title: 'BREATHING IN',
    artist: 'THE REDACTED',
    genre: 'Electronic',
    bpm: 120,
    duration: '3:45',
    mood: ['Chill', 'Atmospheric', 'Dreamy'],
    full_audio_url: `${API_URL}/api/uploads/files/BREATH_IN_1.mp3`,
    cover_art_url: `${API_URL}/api/uploads/files/REDACTED_enigma_duo.png`
  },
  {
    id: 'grime-003',
    title: 'THE GRIME',
    artist: 'THE REDACTED',
    genre: 'Electronic',
    bpm: 140,
    duration: '4:15',
    mood: ['Dark', 'Intense', 'Gritty'],
    full_audio_url: `${API_URL}/api/uploads/files/THE_GRIME.mp3`,
    cover_art_url: `${API_URL}/api/uploads/files/REDACTED_comic_origin.png`
  }
];
